import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Loader2, ClipboardList, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/constants";
import PlanReview from "../components/PlanReview";

const STATUS_TONE = {
  draft: "bg-sky-100 text-sky-800",
  executed: "bg-emerald-100 text-emerald-800",
  executed_with_failures: "bg-amber-100 text-amber-800",
  executing: "bg-amber-100 text-amber-800",
  undone: "bg-slate-100 text-slate-600",
};

export default function PlansPage({ user }) {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [listError, setListError] = useState(null);

  const load = useCallback(async () => {
    setListError(null);
    try {
      const r = await fetch(`${API}/ask-rex/plans?limit=100`, { credentials: "include" });
      if (r.ok) setPlans((await r.json()).plans || []);
      else { setListError(`Could not load plans (error ${r.status}).`); }
    } catch { setListError("Could not load plans. Check your connection and try again."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-4" data-testid="plans-page">
      <div className="flex items-center gap-2">
        <ClipboardList className="w-5 h-5 text-slate-700" />
        <h1 className="text-2xl font-bold text-slate-900">Action plans</h1>
      </div>
      <p className="text-xs text-slate-500 max-w-2xl">Multi-action plans Rex has proposed. Review a draft before approving, and undo any executed batch from here. An undo never overwrites work that changed since the batch ran.</p>

      {planId ? (
        <div className="space-y-3">
          <button onClick={() => navigate("/plans")} className="text-[12px] text-slate-500 hover:underline" data-testid="plans-back">&larr; All plans</button>
          <Card className="border-slate-200"><CardContent className="p-4"><PlanReview planId={planId} onChanged={load} user={user} /></CardContent></Card>
        </div>
      ) : plans === null ? (
        listError ? (
          <div data-testid="plans-list-error">
            <p className="text-sm text-rose-600 flex items-center gap-2"><Loader2 className="w-4 h-4" style={{display:'none'}} />{listError}</p>
            <button onClick={load} className="mt-2 text-[12px] text-sky-700 hover:underline" data-testid="plans-retry">Retry</button>
          </div>
        ) : (
          <div className="text-sm text-slate-600 flex items-center gap-2" data-testid="plans-loading"><Loader2 className="w-4 h-4 animate-spin" />Loading your action plans...</div>
        )
      ) : plans.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="plans-empty">No plans yet. Ask Rex to create or update several records at once and it will propose a plan here.</p>
      ) : (
        <div className="space-y-2" data-testid="plans-list">
          {plans.map((p) => {
            const active = p.rows.filter((r) => r.status !== "dropped").length;
            return (
              <button key={p.plan_id} onClick={() => navigate(`/plans/${p.plan_id}`)}
                className="w-full text-left" data-testid={`plans-item-${p.plan_id}`}>
                <Card className="border-slate-200 hover:border-slate-400 transition-colors">
                  <CardContent className="p-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{p.summary}</p>
                      <p className="text-[11px] text-slate-500">{active} action{active === 1 ? "" : "s"} · {(p.created_at || "").slice(0, 16).replace("T", " ")}{p.created_by_name ? ` · ${p.created_by_name}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`rounded px-2 py-0.5 text-[10px] ${STATUS_TONE[p.status] || "bg-slate-100 text-slate-600"}`}>{p.status.replace(/_/g, " ")}</span>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
